// Models
import { CategoryDefinitionExtensionKeys } from "../../../service/planner/extensions/ICategoryDefinitionExtensionsResource";
import { CategoryFormatting } from "./CategoryFormatting";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
// Utilities
import cloneDeep from "lodash/cloneDeep";
import { tokens } from "@fluentui/react-components";
/**
 * The CategoryDefinition entity implementation.
 */ export class CategoryDefinition extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, source) {
        const mapping = this.getChangePropertyMapping();
        return {
            [mapping.displayName]: changed.displayName,
            [mapping.extensions]: changed.extensions && this.serializeExtensions()
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            extensions: (deltaValue)=>CategoryDefinition.deserializeExtensions(deltaValue)
        });
    }
    static fromResource(resource) {
        return new CategoryDefinition({
            id: resource.id,
            version: resource["@etag"],
            displayName: resource.displayName,
            extensions: this.deserializeExtensions(resource.extensions)
        });
    }
    /**
     * Deserializes the extensions of the CategoryDefinition.
     * The values must be serializable to JSON.
     */ static deserializeExtensions(extensions) {
        const output = {
            categoryFormatting: undefined
        };
        for(const key in CategoryDefinitionExtensionKeys){
            const extensionKey = CategoryDefinitionExtensionKeys[key];
            const serializedData = extensions[extensionKey];
            if (serializedData === undefined) {
                continue;
            }
            if (extensionKey === CategoryDefinitionExtensionKeys.categoryFormatting) {
                try {
                    output.categoryFormatting = CategoryFormatting.fromResource(serializedData);
                } catch  {
                    // TODO: WI 2727337 - Add logging for deserialization errors
                    // Set default formatting when deserialization fails
                    output.categoryFormatting = new CategoryFormatting("transparent", tokens.colorNeutralBackground2, tokens.colorNeutralStroke1, tokens.colorNeutralForeground1);
                }
                continue;
            }
            try {
                output[extensionKey] = JSON.parse(serializedData);
            } catch  {
                continue;
            }
        }
        return output;
    }
    getClone() {
        return new CategoryDefinition({
            id: this.id,
            version: this.version,
            displayName: this.displayName,
            extensions: cloneDeep(this.extensions)
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forCreate() {
        return {
            displayName: this.displayName,
            extensions: this.serializeExtensions()
        };
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            displayName: this.displayName,
            extensions: this.serializeExtensions()
        };
    }
    /**
     * Serializes the extensions of the CategoryDefinition.
     * The values must be serializable to JSON.
     */ serializeExtensions() {
        const output = {};
        // Always serialize to Evo format to support round-trip deserialization across v3 and v4 apis
        // Note: Legacy Categories do not support extensions, so no legacy format serialization is needed
        for(const key in CategoryDefinitionExtensionKeys){
            const extensionKey = CategoryDefinitionExtensionKeys[key];
            if (extensionKey === CategoryDefinitionExtensionKeys.categoryFormatting) {
                if (this.extensions?.categoryFormatting) {
                    const formatting = new CategoryFormatting(this.extensions.categoryFormatting.backgroundColor, this.extensions.categoryFormatting.hoverBackgroundColor, this.extensions.categoryFormatting.hoverBorderColor, this.extensions.categoryFormatting.textColor);
                    const serialized = formatting.forSerialization();
                    if (serialized) {
                        output[extensionKey] = serialized;
                    }
                }
            } else {
                let value = this.extensions[extensionKey];
                if (value && typeof value !== "string") {
                    value = JSON.stringify(value);
                }
                output[extensionKey] = value;
            }
        }
        return output;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.displayName = propertyBag.displayName;
        this.extensions = propertyBag.extensions ?? {};
    }
}
